import React, { useState, useEffect } from 'react';
import {
  Delete as DeleteIcon,
} from '@mui/icons-material';

const SUPPORTED_LANGUAGES = [
  { code: 'default', name: 'Default', flag: '🌐' },
  { code: 'id', name: 'Indonesian', flag: '🇮🇩' },
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' }
];

const MultiLanguageInput = ({ 
  content, 
  onChange, 
  label = "Content", 
  placeholder = "Enter content...",
  multiline = false,
  rows = 3,
  useDefaultLanguage = true, // Whether to use "default" instead of specific language
  legacyDefaultLanguage = 'en' // For backward compatibility when useDefaultLanguage is false
}) => {
  const defaultLanguageCode = useDefaultLanguage ? 'default' : legacyDefaultLanguage;
  const [activeLanguage, setActiveLanguage] = useState(defaultLanguageCode);
  const [languages, setLanguages] = useState([defaultLanguageCode]); // Start with default language
  
  // Initialize content and sync languages with content
  useEffect(() => {
    if (!content) {
      // Initialize with empty default language content
      const initialContent = { [defaultLanguageCode]: '' };
      onChange(initialContent);
      setLanguages([defaultLanguageCode]);
      setActiveLanguage(defaultLanguageCode);
    } else if (typeof content === 'string') {
      // Convert legacy string to object with default language
      const convertedContent = { [defaultLanguageCode]: content };
      onChange(convertedContent);
      setLanguages([defaultLanguageCode]);
      setActiveLanguage(defaultLanguageCode);
    } else if (typeof content === 'object' && content !== null) {
      // Handle migration from legacy specific language to "default"
      const existingLanguages = Object.keys(content);
      
      // If using default language system and no "default" exists, migrate the first language
      if (useDefaultLanguage && !existingLanguages.includes('default') && existingLanguages.length > 0) {
        const firstLangContent = content[existingLanguages[0]];
        const migratedContent = {
          default: firstLangContent,
          ...content
        };
        // Remove the old first language if it was a legacy default
        if (existingLanguages[0] === 'en' || existingLanguages[0] === 'id') {
          delete migratedContent[existingLanguages[0]];
        }
        onChange(migratedContent);
        return; // Let the next useEffect call handle the update
      }
      
      if (existingLanguages.length > 0) {
        // Only update languages if they're different to avoid unnecessary re-renders
        if (JSON.stringify(existingLanguages.sort()) !== JSON.stringify(languages.sort())) {
          setLanguages(existingLanguages);
        }
        
        // Only switch active language if the current one is invalid
        // Don't automatically switch to default language when user is editing other languages
        if (!existingLanguages.includes(activeLanguage)) {
          // Current active language doesn't exist, switch to default if available, otherwise first available
          if (existingLanguages.includes(defaultLanguageCode)) {
            setActiveLanguage(defaultLanguageCode);
          } else {
            setActiveLanguage(existingLanguages[0]);
          }
        }
        // If activeLanguage is valid, keep it as is (don't force switch to default)
      }
    }
  }, [content, onChange, defaultLanguageCode, useDefaultLanguage]);

  const handleContentChange = (value) => {
    const updatedContent = {
      ...content,
      [activeLanguage]: value
    };
    onChange(updatedContent);
  };

  const addLanguage = (languageCode) => {
    if (!languages.includes(languageCode)) {
      setLanguages([...languages, languageCode]);
      // Add empty content for new language
      const updatedContent = {
        ...content,
        [languageCode]: ''
      };
      onChange(updatedContent);
      setActiveLanguage(languageCode);
    }
  };

  const removeLanguage = (languageCode) => {
    // Cannot remove the default language, and must have at least one language
    if (languages.length > 1 && languageCode !== defaultLanguageCode) {
      const updatedLanguages = languages.filter(lang => lang !== languageCode);
      setLanguages(updatedLanguages);
      
      // Remove content for the language
      const updatedContent = { ...content };
      delete updatedContent[languageCode];
      onChange(updatedContent);
      
      // Switch to default language if we removed the active one
      if (activeLanguage === languageCode) {
        setActiveLanguage(defaultLanguageCode);
      }
    }
  };

  const availableLanguagesToAdd = SUPPORTED_LANGUAGES.filter(
    lang => !languages.includes(lang.code) && lang.code !== 'default' // Don't show "default" in add dropdown
  );

  const currentContent = (content && typeof content === 'object') ? content[activeLanguage] || '' : '';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        
        {/* Add Language Dropdown */}
        {availableLanguagesToAdd.length > 0 && (
          <div className="relative">
            <select
              onChange={(e) => {
                if (e.target.value) {
                  addLanguage(e.target.value);
                  e.target.value = ''; // Reset dropdown
                }
              }}
              className="text-xs border border-gray-300 rounded px-2 py-1 text-gray-600"
              defaultValue=""
            >
              <option value="">+ Add Language</option>
              {availableLanguagesToAdd.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Help text for default language */}
      {languages.length > 1 && languages.includes(defaultLanguageCode) && (
        <div className="text-xs text-gray-500 mb-1">
          🌐 = Default language (fallback when user's language is not available)
        </div>
      )}

      {/* Language Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200">
        {languages.map(langCode => {
          const langInfo = SUPPORTED_LANGUAGES.find(l => l.code === langCode);
          const isActive = activeLanguage === langCode;
          const hasContent = content && content[langCode] && content[langCode].trim();
          
          return (
            <div key={langCode} className="flex items-center">
              <button
                type="button"
                onClick={() => setActiveLanguage(langCode)}
                className={`btn ml-2 ${
                  isActive 
                    ? 'btn-primary' 
                    : 'btn-secondary'
                } ${langCode === defaultLanguageCode ? 'bg-blue-50 border-blue-200' : ''}`}
                title={langCode === defaultLanguageCode ? `${langInfo?.name || langCode} (Default Language - used when user's language is not available)` : langInfo?.name || langCode}
              >
                <span className="flex items-center gap-1">
                  {langInfo?.flag || '🌐'} 
                  {langInfo?.name || langCode.toUpperCase()}
                  {hasContent && (
                    <span className="ml-1 w-2 h-2 bg-green-500 rounded-full"></span>
                  )}
                </span>
              </button>
              
              {/* Remove Language Button (except for required language) */}
              {/* Remove Language Button (except for default language) */}
              {langCode !== defaultLanguageCode && languages.length > 1 ? (
                <button
                  type="button"
                  onClick={() => removeLanguage(langCode)}
                  className="btn btn-danger"
                  title={`Remove ${langInfo?.name || langCode}`}
                >
                  <DeleteIcon fontSize="small" />
                </button>
              ) : langCode === defaultLanguageCode && languages.length > 1 ? (
                <span 
                  className="ml-1 p-1 text-blue-500 cursor-help"
                  title={`${langInfo?.name || langCode} is the default language and cannot be removed`}
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Content Input */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {SUPPORTED_LANGUAGES.find(l => l.code === activeLanguage)?.flag || '🌐'} 
            {' '}
            {SUPPORTED_LANGUAGES.find(l => l.code === activeLanguage)?.name || activeLanguage.toUpperCase()}
          </span>
          
          {currentContent && (
            <span className="text-xs text-gray-500">
              {currentContent.length} characters
            </span>
          )}
        </div>
        
        {multiline ? (
          <textarea
            value={currentContent}
            onChange={(e) => handleContentChange(e.target.value)}
            placeholder={placeholder}
            rows={rows}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 resize-vertical"
          />
        ) : (
          <input
            type="text"
            value={currentContent}
            onChange={(e) => handleContentChange(e.target.value)}
            placeholder={placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        )}
      </div>

      {/* Content Preview for Other Languages */}
      {languages.length > 1 && (
        <div className="mt-3 p-3 bg-gray-50 rounded-md">
          <h4 className="text-xs font-medium text-gray-700 mb-2">Content in Other Languages:</h4>
          <div className="space-y-1">
            {languages
              .filter(lang => lang !== activeLanguage)
              .map(langCode => {
                const langInfo = SUPPORTED_LANGUAGES.find(l => l.code === langCode);
                const langContent = content && content[langCode] ? content[langCode] : '';
                
                return (
                  <div key={langCode} className="flex items-start gap-2 text-xs">
                    <span className="flex-shrink-0 text-gray-500">
                      {langInfo?.flag || '🌐'} {langInfo?.name || langCode.toUpperCase()}:
                    </span>
                    <span className="text-gray-600 flex-1 truncate">
                      {langContent || <em className="text-gray-400">No content</em>}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiLanguageInput; 